import './styles.css';

import React, { useCallback, useState, RefObject } from 'react';
import { Tooltip, Badge, Button, useTooltip, Typography } from '@vtblife/uikit';
import type { TooltipProps } from '@vtblife/uikit/dist/components/tooltip/types';
import Icon from '@vtblife/uikit-icons';

import { FSGallery } from '@search/vtbeco-frontend-core/view/common/components/gallery/GalleryDesktop/FSGallery/FSGallery';
import { VasesIcons } from '@search/vtbeco-frontend-core/view/common/components/VasesIcons';
import { ImageAttributesTypeEnum } from '@search/vtbeco-frontend-core/enums';
import { SizeThumbnail, FigcaptionPosition, FigcaptionPositionColor } from '@search/vtbeco-frontend-core/view/common/components/gallery/GalleryDesktop/constants';
import classname from '@search/classname/src';
import { useClassifiedOfferDataLayerPush } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedOfferGtmContext';
import { useImageLazySrcSet } from '@search/vtbeco-frontend-core/domain/image/lazy/ImageLazySrcSet';
import badgeLabel from '@search/vtbeco-frontend-core/dict/offer-badge';
import { MapThumbnail } from '../MapThumbnail';
import CarouselGallery from './CarouselGallery';
import mock from './stories/mock.json';

const cn = classname.bind(null, 'GalleryOfferCard');

interface IHoverTooltipProps extends Omit<TooltipProps, 'to'> {
    visible: boolean;
    text: string;
}

const HoverTooltip: React.FC<IHoverTooltipProps> = ({
    text,
    visible,
    children,
    ...props
}) => {
    const { to, registerControl } = useTooltip();

    return (
        <div ref={registerControl} className={cn('btn')}>
            {children}
            {visible ? (
                <Tooltip
                    {...props}
                    to={to}
                >{text}</Tooltip>
            ) : null}
        </div>
    );
};

export interface IImage {
    id: string | number;
    originPath?: string;
    src?: string;
    src2x?: string;
    large?: string;
    description: string;
    attributesType?: keyof typeof ImageAttributesTypeEnum | null;
}

interface IGalleryOfferCardProps {
    thumbnails: IImage[];
    isShowPanorama: boolean;
    hasTopPriority?: boolean;
    hasHighlightPriority?: boolean;
    isPledge?: boolean;
    onlineView?: boolean;
    isPit?: boolean;
    offerTitle?: string;
    onOpenPanorama?: () => void;
    mapRef: RefObject<HTMLDivElement | null>;
    starterBanner?: React.ReactNode;
    repairBanner?: React.ReactNode;
    className?: string;
}

const GalleryOfferCard: React.FC<IGalleryOfferCardProps> = ({
    className,
    thumbnails = mock.thumbnails as IImage[],
    starterBanner,
    repairBanner,
    isShowPanorama,
    hasHighlightPriority,
    hasTopPriority,
    isPledge,
    onlineView,
    isPit,
    onOpenPanorama: handleOpenPanorama,
    mapRef
}) => {
    const [ isFSGalleryOpen, setIsFSGAlleryOpen ] = useState(false);
    const [ clickedImgIndex, setClickedImgIndex ] = useState(1);
    const [ isOpenRepairForm, setIsOpenRepairForm ] = useState(false);
    const getSrcSet = useImageLazySrcSet();
    const { to, registerControl } = useTooltip();

    const thumbnailsFS = thumbnails
        .reduce((acc, photo) => {
            // src: photo.preview2,
            // src2x: photo.medium,
            // large: photo.large,
            const previewUrl = photo.originPath ?
                getSrcSet({ originPath: photo.originPath, width: 140, height: 105 }).src :
                undefined;

            const originUrl = photo.originPath ?
                getSrcSet({ originPath: photo.originPath, width: 2240, height: 1680 }).src :
                undefined;

            acc.preview.push(previewUrl ?? photo.src);
            acc.origin.push(originUrl ?? photo.large ?? photo.src2x);
            acc.attributesType.push(photo.attributesType);

            return acc;
        }, {
            preview: [] as (string | undefined)[],
            origin: [] as (string | undefined)[],
            attributesType: [] as ('UNKNOWN' | 'LAYOUT' | null | undefined)[]
        });

    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const layoutImageIndex = thumbnails.findIndex((t: IImage) => t.attributesType === ImageAttributesTypeEnum.LAYOUT);

    const handleClickOnImage = useCallback((index: number) => {
        setIsFSGAlleryOpen(true);
        setClickedImgIndex(index);
    }, [ setIsFSGAlleryOpen ]);

    const handleClickOnLayout = useCallback(
        () => {
            setIsFSGAlleryOpen(true);
            setClickedImgIndex(layoutImageIndex);
        },
        [ setIsFSGAlleryOpen, layoutImageIndex ]
    );

    const handleClickOnRepair = useCallback(() => {
        dataLayerPush({
            event: 'up_action',
            theme: 'remont',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            page_type: 'card',
            type: 'to_gallery_click'
        });

        setIsFSGAlleryOpen(true);
        setIsOpenRepairForm(true);
    }, [ dataLayerPush, setIsFSGAlleryOpen, setIsOpenRepairForm ]);

    const handleCloseGallery = useCallback(() => {
        setIsFSGAlleryOpen(false);
        setIsOpenRepairForm(false);
    }, [ setIsFSGAlleryOpen, setIsOpenRepairForm ]);

    const isShowButtonsBlock = thumbnails.length > 1 || layoutImageIndex >= 0 || isShowPanorama;

    return (
        <>
            <div className={cn(null, undefined, className)}>
                <link itemProp='image' href={thumbnailsFS.origin?.[0]} />
                <CarouselGallery
                    elements={thumbnails}
                    onClickMain={handleClickOnImage}
                />
                <div className={cn('badges')}>
                    {onlineView && (
                        <div ref={registerControl}>
                            <Badge color='purple' size='l'>
                                {badgeLabel.onlineView}
                                <span style={{ marginLeft: '8px' }}>
                                    <Icon
                                        name='exclamation-circle'
                                        size='s'
                                        color='rgba(255, 255, 255, 0.64)'
                                    />
                                </span>
                            </Badge>
                            <Tooltip placement='body' to={to}>
                                {/* eslint-disable-next-line max-len */}
                                Продавец готов показать объект по&nbsp;видеозвонку. Договоритесь о&nbsp;времени и&nbsp;приложении, в&nbsp;котором будет удобно пообщаться.
                            </Tooltip>
                        </div>
                    )}
                    {isPledge && (
                        <Badge color='purple' size='l'>
                            В залоге
                        </Badge>
                    )}
                    {isPit && (
                        <Badge color='purple' size='l'>
                            Имущественные торги
                        </Badge>
                    )}
                </div>
                {(hasTopPriority || hasHighlightPriority) && <VasesIcons
                    className={cn('priorities')}
                    hasTop={hasTopPriority}
                    hasHightlight={hasHighlightPriority}
                />}
                <MapThumbnail
                    mapRef={mapRef}
                    onClick={() => dataLayerPush({ event: 'classified_click_offer_but_map' })}
                />
                {isShowButtonsBlock && (
                    <div className={cn('buttons')}>
                        {layoutImageIndex >= 0 && (
                            <HoverTooltip text='Показать планировку' visible direction='down'>
                                <Button
                                    icon='blueprint'
                                    variant='primary-alt-bg'
                                    size='s'
                                    shadow
                                    onClick={() => {
                                        handleClickOnLayout();
                                        dataLayerPush({ event: 'classified_click_offer_but_planirovka' });
                                    }}
                                >
                                    Планировка
                                </Button>
                            </HoverTooltip>
                        )}
                        {isShowPanorama && (
                            <HoverTooltip text='Открыть панораму' visible direction='down'>
                                <Button
                                    icon='panorama'
                                    variant='primary-alt-bg'
                                    size='s'
                                    shadow
                                    onClick={() => {
                                        handleOpenPanorama?.();
                                        dataLayerPush({ event: 'classified_click_offer_but_panorama' });
                                    }}
                                >
                                    Панорама улицы
                                </Button>
                            </HoverTooltip>
                        )}
                        {thumbnails.length > 1 && (
                            <HoverTooltip text='Открыть галерею' visible direction='down'>
                                <Button
                                    icon='camera'
                                    variant='primary-alt-bg'
                                    size='s'
                                    shadow
                                    onClick={() => handleClickOnImage(0)}
                                >
                                    {thumbnails.length}
                                </Button>
                            </HoverTooltip>
                        )}
                        {thumbnails.length > 0 && repairBanner && (
                            <HoverTooltip visible text='Рассчитать ремонт' direction='down'>
                                <Button
                                    variant='primary-alt-bg'
                                    size='s'
                                    shadow
                                    onClick={handleClickOnRepair}
                                >
                                    Расчёт ремонта
                                </Button>
                            </HoverTooltip>
                        )}
                    </div>
                )}
                {thumbnails.length === 0 && (
                    <Typography
                        color='secondary'
                        variant='secondary-alone'
                        className={cn('emptyText', { isShowButtonsBlock })}
                    >
                        Фотографий нет
                    </Typography>
                )}
            </div>
            {isFSGalleryOpen && (<FSGallery
                belowModal
                starterBanner={starterBanner}
                repairBanner={repairBanner}
                onClose={handleCloseGallery}
                srcImages={thumbnailsFS}
                initialIndexSrcImage={clickedImgIndex}
                isOpenRepairForm={isOpenRepairForm}
                size={SizeThumbnail.L}
                figcaptionPosition={FigcaptionPosition.TOP}
                figcaptionPositionColor={FigcaptionPositionColor.WHITE}
                withCount
            />)}
        </>
    );
};

export default GalleryOfferCard;
